import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Sentry from '@sentry/nextjs';
import { RootState } from 'app/reducer';
import BlogLayout from 'app/components/blog/BlogLayout';
import { MobileOnly, DesktopOnly } from 'app/components/misc/Layout';
import SubscriptionForm from 'app/components/blog/SubscriptionForm';
import { getCommentsRequest } from 'entity/comment/commentRedux';
import { useGetPaginationByType } from 'entity/comment/commentHooks';
import { openShareModal } from 'dispatcher/modalDispatcher';
import {
  ArticleDetail,
  RelatedArticleSection,
  AmpAnalytics,
  ViewNonAmpLink,
} from 'app/components/blog/article';
import { ArticleWithLink } from 'app/entity/blog/blogTypes';
import useIsNativeApp from 'utils/hooks/useIsNativeApp';
import { useAmp } from 'next/amp';
import blogApi from 'app/entity/blog/blogApi';
import { useRouter } from 'next/router';
import useOnScreen from 'utils/hooks/useOnScreen';
import useIsScrolling from 'utils/hooks/useIsScrolling';
import mixpanel, { trackedEvents } from 'utils/mixpanel';
import { SAVE_TYPE } from 'constants/app';
import BookmarkButton from 'components/post/BookmarkButton';
import CommentOverlay from 'app/components/blog/article/CommentOverlay';
import CommentButton from 'app/components/blog/article/CommentButton';
import buildConfig from 'utils/buildConfig';
import { SHARE_TYPE } from 'app/constants/app';
import getShareCount from 'app/utils/getShareCount';
import { ArticlePageContext } from '../context/ArticlePageContext';
import PopularSection from '../../../components/blog/PopularSection';

const SectionItem = props => {
  return (
    <div className="mb-4 flex flex-col justify-center relative w-full">
      {props.children}
    </div>
  );
};

interface OwnProps {
  article: ArticleWithLink;
  relatedArticles: ArticleWithLink[];
}

/**
 * Article page is a hybrid AMP page.
 * We have to ensure that there are no errors in terminal
 * when on article page in AMP mode in development
 * (by adding ?amp=1 to the URL)
 */
const ArticleContent = (props: OwnProps) => {
  const { article, relatedArticles } = props;
  const router = useRouter();
  const { rcid, pcid } = router.query;

  const dispatch = useDispatch();

  // Setting articleId and commentsCount which included in postgresDB
  const [articleOwnId, setArticleOwnId] = useState(null);
  const [isCommentOpen, setCommentOpen] = useState<boolean>(false);
  const isScrolling = useIsScrolling();

  const isFetching = useSelector(
    (state: RootState) => state.comment.api.isFetching,
  );
  const isNativeApp = useIsNativeApp();
  const { pagination } = useGetPaginationByType('Article', articleOwnId);

  const commentSectionRef = useRef(null);
  const { isOnScreen, isAboveScreen } = useOnScreen(commentSectionRef);

  // Refs
  const isAmp = useAmp();

  const handleCommentClose = () => {
    setCommentOpen(false);
  };

  const handleCommentOpen = () => {
    mixpanel.track(trackedEvents.BlogCommentButtonClicked, {
      param: 'share',
    });
    setCommentOpen(true);
  };

  // fetch comments
  useEffect(() => {
    const fetchBlogId = async () => {
      const articleResponse = await blogApi.getArticle(article.slug);
      if (articleResponse.ok) {
        dispatch(
          getCommentsRequest({
            commentableType: 'Article',
            commentableId: articleResponse.data.data.id,
            page: 1,
            per: 20,
            ...(pcid && { pcid }),
            ...(rcid && { rcid }),
          }),
        );
        setArticleOwnId(articleResponse.data.data.id);
      }
    };

    try {
      fetchBlogId();
    } catch (error) {
      Sentry.captureException(error);
      setArticleOwnId(null);
    }
  }, [article?.slug, dispatch]);

  useEffect(() => {
    if (isCommentOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isCommentOpen]);

  const showCommentButton =
    !isFetching &&
    pagination.currentPage > 0 &&
    !isOnScreen &&
    !isAboveScreen &&
    !isScrolling;

  const providerValue = useMemo(() => {
    return { article };
  }, [article]);

  const articleLink = `${buildConfig.blogHost}/${article.slug}`;
  const shareCount = getShareCount(article.engagement);

  return (
    <ArticlePageContext.Provider value={providerValue}>
      <BlogLayout stickySidebarDisabled showGoogleAds>
        <AmpAnalytics />
        <SectionItem>
          <ArticleDetail
            article={article}
            articleOwnId={articleOwnId}
            commentSectionRef={<div ref={commentSectionRef} />}
          />
          {showCommentButton && (
            <CommentButton
              handleCommentOpen={handleCommentOpen}
              commentsCount={pagination.totalCount}
              bookmarkButton={
                <BookmarkButton
                  contentId={article.id}
                  slug={article.slug}
                  type={SAVE_TYPE.ARTICLE}
                  onClickTrack={() => {
                    mixpanel.track(trackedEvents.BlogCommentButtonClicked, {
                      param: 'bookmark',
                    });
                  }}
                />
              }
              shareCount={shareCount}
              onShareClick={() => {
                mixpanel.track(trackedEvents.BlogCommentButtonClicked, {
                  param: 'share',
                });
                dispatch(
                  openShareModal(SHARE_TYPE.ARTICLE, articleLink, null, {
                    imageUrl: article.imageUrl,
                    title: article.title,
                  }),
                );
              }}
            />
          )}
        </SectionItem>
        {!isAmp && !isNativeApp && (
          <MobileOnly>
            <SectionItem>
              <PopularSection />
            </SectionItem>
            {relatedArticles.length > 0 && (
              <SectionItem>
                <p className="text-3xl py-8 px-4">Related Articles</p>
                <RelatedArticleSection articles={relatedArticles} />
              </SectionItem>
            )}
            <SectionItem>
              <SubscriptionForm />
            </SectionItem>
          </MobileOnly>
        )}
        <DesktopOnly>
          {relatedArticles.length > 0 && (
            <SectionItem>
              <p className="text-3xl py-8 px-4">Related Articles</p>
              <RelatedArticleSection articles={relatedArticles} />
            </SectionItem>
          )}
        </DesktopOnly>
        <ViewNonAmpLink slug={`/${article.slug}`} />
        <CommentOverlay
          isCommentOpen={isCommentOpen}
          handleCommentClose={handleCommentClose}
          articleOwnId={articleOwnId}
          article={article}
        />
      </BlogLayout>
    </ArticlePageContext.Provider>
  );
};

export default ArticleContent;
